export function contentToText(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (!Array.isArray(value)) {
    if (typeof value === "object") {
      const raw = value.message ?? value.content ?? value.text ?? "";
      return typeof raw === "string" ? raw : contentToText(raw);
    }
    return "";
  }

  const textItems = value.filter((item) => item?.type === "text" && item?.text);
  if (textItems.length > 0) {
    return textItems.map((item) => item.text).join("\n\n");
  }
  const fallback = value.find((item) => item?.text || item?.content || item?.message);
  return fallback?.text || fallback?.content || fallback?.message || "";
}

function stripHtmlTags(value) {
  if (typeof value !== 'string') return '';
  return value
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<\/?[^>]+>/g, ' ');
}

function decodeCommonHtmlEntities(value) {
  if (typeof value !== 'string') return '';
  return value
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

export function detectContentFormat(value) {
  if (typeof value !== 'string') return 'text';
  const trimmed = value.trim();
  if (!trimmed) return 'text';

  if (/<\/?[a-z][\s\S]*>/i.test(trimmed)) return 'html';

  const markdownPatterns = [
    /^\s{0,3}#{1,6}\s+/m,
    /^\s*[-*+]\s+/m,
    /^\s*\d+\.\s+/m,
    /\*\*[^*]+\*\*/,
    /_[^_]+_/,
    /`[^`]+`/,
    /\[[^\]]+\]\([^)]+\)/,
    /^\s*>\s+/m,
  ];
  if (markdownPatterns.some((pattern) => pattern.test(trimmed))) return 'markdown';

  return 'text';
}

function pickRichestContent(candidates) {
  const scored = candidates
    .filter((item) => typeof item === 'string' && item.trim())
    .map((item) => {
      const format = detectContentFormat(item);
      const score = format === 'html' ? 3 : format === 'markdown' ? 2 : 1;
      return { value: item, format, score };
    })
    .sort((a, b) => b.score - a.score);

  return scored[0] ?? null;
}

export function resolveMessageSourceContent(message) {
  if (!message) return '';

  const directContent = Array.isArray(message.content)
    ? contentToText(message.content)
    : typeof message.content === 'string'
      ? message.content
      : '';

  const richest = pickRichestContent([
    message.sourceContent,
    message.formattedText,
    message.messageText,
    message.message,
    directContent,
    message.text,
    message.body,
  ]);

  if (richest) return richest.value;
  return directContent || '';
}

const DEFAULT_DOCUMENT_TITLE = "Document";

/**
 * Extracts a document title from message content (same logic as old canvas).
 * Uses markdown heading, then first line (5-50 chars), then default.
 */
export function extractDocumentTitle(content) {
  const text = typeof content === "string" ? content : contentToText(content);
  if (!text || !text.trim()) return DEFAULT_DOCUMENT_TITLE;

  const headingMatch = text.match(/^\s*#{1,3}\s+([^\n]+)/);
  if (headingMatch) return headingMatch[1].trim();

  const firstLineMatch = text.match(/^([^\n]{5,50})\n/);
  if (firstLineMatch) return firstLineMatch[1].trim();

  return DEFAULT_DOCUMENT_TITLE;
}

export function isDocumentLikeContent(content) {
  if (content == null) return false;

  const text = typeof content === "string" ? content : contentToText(content);
  return typeof text === "string" && text.length > 200;
}

/**
 * Short description for document cards: first line/paragraph, capped at maxLength.
 */
export function extractDocumentDescription(content, maxLength = 120) {
  const text = typeof content === "string" ? content : contentToText(content);
  if (!text || !text.trim()) return "";

  const withoutMarkdown = text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/!\[[^\]]*]\([^)]+\)/g, ' ')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/^\s*#{1,6}\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/^\s*>\s?/gm, '');
  const normalized = decodeCommonHtmlEntities(stripHtmlTags(withoutMarkdown))
    .replace(/\r?\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return "";

  if (normalized.length <= maxLength) return normalized;
  return normalized.slice(0, maxLength).trim() + "…";
}
